import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of, startWith } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	map,
	switchMap,
} from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.services';
import { EditClient } from '../add-client-details/add-client-details.component';
import {
	FormControl,
	FormGroup,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import Property = Components.Schemas.Location;
import { Timezone } from '../../../models/timezone';
import { Country } from '../../../models/country';
import { GeoService } from '../../../services/geo.service';
import { MatDivider } from '@angular/material/list';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatIcon } from '@angular/material/icon';
import {
	MatFormField,
	MatLabel,
	MatHint,
	MatPrefix,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';

export enum PropertySelectionType {
	newProperty = 'newProperty',
	existing = 'existing',
}

type PropertyResult = { property: Property; deviceCount: number };

@Component({
	selector: 'app-add-client-property-setup',
	templateUrl: './add-client-property-setup.component.html',
	styleUrls: [
		'./add-client-property-setup.component.scss',
		'../../common/main.scss',
		'../../common/stepper.scss',
	],
	imports: [
		MatDivider,
		MatRadioGroup,
		FormsModule,
		MatRadioButton,
		MatIcon,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatAutocompleteTrigger,
		MatAutocomplete,
		MatOption,
		MatHint,
		MatPrefix,
		MatSuffix,
		AsyncPipe,
	],
})
export class AddClientPropertySetupComponent implements OnInit {
	@Input() editClient!: EditClient;
	@Input() propertySelectionType?: PropertySelectionType;
	@Input() preselectedProperty?: PropertyResult; // Property preselected from another page
	@Output() propertySelectionTypeChange =
		new EventEmitter<PropertySelectionType>();
	@Output() propertySelected = new EventEmitter<PropertyResult | undefined>();

	PropertySelectionType = PropertySelectionType;

	// Local variable for radio button selection (not yet committed)
	selectedType?: PropertySelectionType;
	// Flag to track if user has confirmed their selection by pressing Continue
	hasConfirmedSelection = false;

	addClientWithNewPropertyForm = new FormGroup({
		propertyName: new FormControl('', [
			Validators.required,
			Validators.minLength(1),
		]),
		timezone: new FormControl<string | Timezone>('', [Validators.required]),
		address: new FormControl('', [
			Validators.required,
			Validators.minLength(1),
		]),
		apartment: new FormControl(''),
		town: new FormControl('', [Validators.required, Validators.minLength(1)]),
		state: new FormControl('', [Validators.required]),
		postalCode: new FormControl('', [
			Validators.required,
			Validators.minLength(1),
		]),
		country: new FormControl<string | Country>('', [Validators.required]),
	});

	propertySearchControl = new FormControl('');
	filteredProperties: Observable<PropertyResult[]> = of([]);
	selectedProperty?: PropertyResult;
	showPropertyDetails = false;

	timezones!: Timezone[];
	countries!: Country[];
	filteredCountries!: Observable<Country[]> | undefined;
	filteredTimezones!: Observable<Timezone[]> | undefined;
	showCountryError = false;
	showTimezoneError = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly geoService: GeoService
	) {}

	async ngOnInit(): Promise<void> {
		this.timezones = await this.apiService.getTimezone();
		this.countries = this.geoService.allCountries;

		// If property is preselected, set it up and mark as confirmed
		if (this.preselectedProperty) {
			this.selectedProperty = this.preselectedProperty;
			this.selectedType = PropertySelectionType.existing;
			this.hasConfirmedSelection = true;
			this.showPropertyDetails = true;
			this.propertySelected.emit(this.preselectedProperty);
		}

		// Set up property search with debouncing
		this.filteredProperties = this.propertySearchControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged(),
			switchMap((value) => {
				if (typeof value === 'string' && value.length >= 2) {
					return this.searchProperties(value);
				}
				return of([]);
			})
		);

		// Filter Timezone
		this.filteredTimezones = this.addClientWithNewPropertyForm
			.get('timezone')
			?.valueChanges.pipe(
				debounceTime(300),
				startWith(''),
				map((value) => {
					if (typeof value === 'string') {
						return this._filterTimezone(value || '');
					} else {
						return this._filterTimezone(value?.name || '');
					}
				})
			);

		// Filter contries
		this.filteredCountries = this.addClientWithNewPropertyForm
			.get('country')
			?.valueChanges.pipe(
				debounceTime(300),
				startWith(''),
				map((value) => {
					if (typeof value === 'string') {
						return this._filterCountry(value || '');
					} else {
						return this._filterCountry(value?.name || '');
					}
				})
			);
	}

	public get isValid(): boolean {
		// Phase 1: User must select a radio button option
		if (!this.hasConfirmedSelection) {
			return !!this.selectedType;
		}

		// Phase 2: After confirming selection, validate based on type
		if (this.propertySelectionType === PropertySelectionType.newProperty) {
			// New property button is in scope but functionality is out of scope
			// Return true to enable Continue button (will be handled in main component)
			return this.addClientWithNewPropertyForm.valid;
		}

		if (this.propertySelectionType === PropertySelectionType.existing) {
			// User must select a property from the dropdown
			return !!this.selectedProperty;
		}

		return false;
	}

	public confirmSelection(): void {
		if (this.selectedType) {
			this.hasConfirmedSelection = true;
			this.propertySelectionType = this.selectedType;
			this.propertySelectionTypeChange.emit(this.selectedType);
		}
	}

	onPropertySelectionTypeChange(type: PropertySelectionType): void {
		this.propertySelectionType = type;
		this.propertySelectionTypeChange.emit(type);

		// Clear property selection when switching types
		this.selectedProperty = undefined;
		this.showPropertyDetails = false;
		this.propertySearchControl.setValue('');
		this.propertySelected.emit(undefined);
	}

	async searchProperties(searchTerm: string): Promise<PropertyResult[]> {
		try {
			return await this.apiService.getProperties([
				{ param: 'propertyName', value: searchTerm },
				{ param: 'ownedOnly', value: 'true' },
				{ param: 'rowIndex', value: 0 },
				{ param: 'rowCount', value: 30 },
			]);
		} catch (error) {
			console.error('Error searching properties:', error);
			return [];
		}
	}

	onPropertySelected(property: PropertyResult): void {
		this.selectedProperty = property;
		this.showPropertyDetails = true;
		this.propertySelected.emit(property);
	}

	displayPropertyName(property: PropertyResult | string): string {
		if (typeof property === 'string') {
			return property;
		}
		return property?.property.name || '';
	}

	formatAddress(property: PropertyResult): string {
		const parts: string[] = [];
		const addr = property.property.address;

		if (addr?.address2) {
			parts.push(addr.address2);
		}
		if (addr?.address1) {
			parts.push(addr.address1);
		}
		if (addr?.locality) {
			parts.push(addr.locality);
		}
		if (addr?.stateProvinceRegion) {
			parts.push(addr.stateProvinceRegion);
		}
		if (addr?.postalCode) {
			parts.push(addr.postalCode);
		}

		return parts.join(', ');
	}

	private _filterCountry(value: string): Country[] {
		const filterValue = value.toLowerCase();

		return this.countries.filter((option) => {
			return option.name.toLowerCase().includes(filterValue);
		});
	}

	displayTimezoneName(timezone: string | Timezone): string {
		if (!timezone) {
			return '';
		}

		if (typeof timezone === 'string') {
			const foundTimezone = this.timezones?.find(
				(res) => res.timezone === timezone
			);
			return foundTimezone
				? foundTimezone.timezone + ' - ' + foundTimezone.description
				: '';
		}

		return timezone.timezone
			? timezone.timezone + ' - ' + timezone.description
			: '';
	}

	displayCountryName(coutnry: Country): string {
		return coutnry && coutnry.name ? coutnry.name : '';
	}

	filterCountries(value: any): void {
		this.showCountryError = true;
		this.addClientWithNewPropertyForm
			.get('country')
			?.setErrors({ notValid: true });
		this.filteredCountries = of(this._filterCounties(value));
	}

	filterTimezones(value: string | Event): void {
		this.showTimezoneError = true;
		this.addClientWithNewPropertyForm
			.get('timezone')
			?.setErrors({ notValid: true });
		const filterValue =
			typeof value === 'string'
				? value
				: (value.target as HTMLInputElement).value;
		this.filteredTimezones = of(this._filterTimezone(filterValue));
	}

	openAutocompleteCountries(): void {
		this.filteredCountries = of(this.geoService.allCountries);
	}

	openAutocompleteTimezones(): void {
		this.filteredTimezones = of(this.timezones);
	}

	private _filterCounties(value: string): Country[] {
		if (!value) {
			return this.countries;
		}
		const filterValue = value.toLowerCase();
		return this.geoService.allCountries.filter((country) =>
			country?.name?.toLowerCase()?.includes(filterValue)
		);
	}

	private _filterTimezone(value: string): Timezone[] {
		if (!value) {
			return this.timezones;
		}
		const filterValue = value.toLowerCase();
		return this.timezones?.filter(
			(tz) =>
				tz.timezone.toLowerCase().includes(filterValue) ||
				tz.description.toLowerCase().includes(filterValue)
		);
	}

	timezoneSelected(): void {
		// show the error if user does not select a country from the list per the requirements
		this.addClientWithNewPropertyForm.get('timezone')?.setErrors(null);
		this.showTimezoneError = false;
	}

	countrySelected(): void {
		this.addClientWithNewPropertyForm.get('country')?.setErrors(null);
		this.showCountryError = false;
	}
}
